/**
 * Redis Client Configuration
 * 
 * Provides a singleton Redis client instance using Upstash Redis REST API.
 * This client works in browser environments without requiring a backend server.
 * 
 * Features:
 * - REST API based (works from browser)
 * - Automatic connection management
 * - Error handling and logging
 * - Environment-based configuration
 * - Lazy initialization to avoid browser compatibility issues
 */

// Helper to check if we're in development mode (works in both Vite and Node.js)
const isDevelopmentMode = () => {
  try {
    if (import.meta && import.meta.env?.MODE === 'development') {
      return true;
    }
  } catch (e) {
    // Ignore
  }
  
  try {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      return true;
    }
  } catch (e) {
    // Ignore
  }
  
  return false;
};

class RedisClient {
  constructor() {
    this.client = null;
    this.isEnabled = false;
    this.initialized = false;
    this.Redis = null;
    this.importPromise = null;
  }

  /**
   * Lazy load Redis library
   */
  async loadRedis() {
    if (this.Redis) {
      return this.Redis;
    }

    if (this.importPromise) {
      return this.importPromise;
    }

    this.importPromise = (async () => {
      try {
        // Check if we're in a browser environment
        const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
        
        // Check if process is available (needed by @upstash/redis)
        const hasProcess = typeof process !== 'undefined';
        
        if (isBrowser && !hasProcess) {
          // In browser without Node.js polyfills - Redis won't work
          if (isDevelopmentMode()) {
            console.warn('[Redis] Skipping Redis initialization in browser environment without Node.js polyfills');
          }
          return null;
        }

        const redisModule = await import('@upstash/redis');
        this.Redis = redisModule.Redis;
        return this.Redis;
      } catch (error) {
        if (isDevelopmentMode()) {
          console.warn('[Redis] Failed to load Redis library:', error?.message);
        }
        return null;
      }
    })();

    return this.importPromise;
  }

  /**
   * Initialize the Redis client (lazy initialization)
   */
  async initializeClient() {
    // Prevent multiple initialization attempts
    if (this.initialized) {
      return;
    }
    
    this.initialized = true;

    try {
      // Support both Vite (import.meta.env) and Node.js (process.env)
      const getEnvVar = (key) => {
        try {
          // Try Vite first (browser/build time)
          if (import.meta && import.meta.env) {
            return import.meta.env[key];
          }
        } catch (e) {
          // Ignore
        }
        
        // Try Node.js process (server/test)
        try {
          if (typeof process !== 'undefined' && process.env) {
            return process.env[key] || process.env[key.replace('VITE_', '')];
          }
        } catch (e) {
          // Ignore
        }
        
        return undefined;
      };

      const redisUrl = getEnvVar('VITE_UPSTASH_REDIS_REST_URL');
      const redisToken = getEnvVar('VITE_UPSTASH_REDIS_REST_TOKEN');

      // Check credentials FIRST before loading the library
      if (!redisUrl || !redisToken) {
        if (isDevelopmentMode()) {
          console.debug('[Redis] Credentials not configured. Redis caching disabled.');
        }
        this.isEnabled = false;
        this.client = null;
        return;
      }

      // Only load Redis if we have credentials
      const Redis = await this.loadRedis();
      
      if (!Redis) {
        if (isDevelopmentMode()) {
          console.debug('[Redis] Redis library not available. Caching disabled.');
        }
        this.isEnabled = false;
        this.client = null;
        return;
      }

      // Wrap Redis initialization in a try-catch to handle any browser-specific errors
      try {
        this.client = new Redis({
          url: redisUrl,
          token: redisToken,
        });

        this.isEnabled = true;
        
        // Log only in development
        if (isDevelopmentMode()) {
          console.debug('[Redis] Client initialized successfully');
        }
      } catch (redisInitError) {
        // Redis initialization failed (might be browser environment issues)
        if (isDevelopmentMode()) {
          console.debug('[Redis] Failed to initialize Redis client:', redisInitError?.message);
        }
        this.isEnabled = false;
        this.client = null;
      }
    } catch (error) {
      if (isDevelopmentMode()) {
        console.debug('[Redis] Unexpected error during initialization:', error?.message);
      }
      this.isEnabled = false;
      this.client = null;
    }
  }

  /**
   * Get the Redis client instance
   */
  async getClient() {
    // Lazy initialize if not done yet
    if (!this.initialized) {
      await this.initializeClient();
    }
    
    if (!this.isEnabled) {
      return null;
    }
    return this.client;
  }

  /**
   * Check if Redis is enabled and available
   */
  async isAvailable() {
    // Lazy initialize if not done yet
    if (!this.initialized) {
      await this.initializeClient();
    }
    
    return this.isEnabled && this.client !== null;
  }

  /**
   * Ping Redis to check connection
   */
  async ping() {
    if (!(await this.isAvailable())) {
      return false;
    }

    try {
      const result = await this.client.ping();
      return result === 'PONG';
    } catch (error) {
      console.error('[Redis] Ping failed:', error);
      return false;
    }
  }

  /**
   * Get a value from Redis
   * @param {string} key - The key to retrieve
   * @returns {Promise<any>} The value or null if not found
   */
  async get(key) {
    if (!(await this.isAvailable())) {
      return null;
    }

    try {
      const value = await this.client.get(key);
      
      if (isDevelopmentMode()) {
        console.debug(`[Redis] GET ${key}:`, value ? 'HIT' : 'MISS');
      }
      
      return value;
    } catch (error) {
      // Silently fail - Redis is optional, app continues without caching
      // Only log if not a network error
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[Redis] GET ${key} failed:`, error?.message);
      }
      return null;
    }
  }

  /**
   * Set a value in Redis with optional TTL
   * @param {string} key - The key to set
   * @param {any} value - The value to store
   * @param {number} [ttl] - Time to live in seconds
   */
  async set(key, value, ttl) {
    if (!(await this.isAvailable())) {
      return false;
    }

    try {
      if (ttl) {
        await this.client.setex(key, ttl, value);
      } else {
        await this.client.set(key, value);
      }
      
      if (isDevelopmentMode()) {
        console.debug(`[Redis] SET ${key}${ttl ? ` (TTL: ${ttl}s)` : ''}`);
      }
      
      return true;
    } catch (error) {
      // Silently fail - Redis is optional, app continues without caching
      // Only log if not a network error
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[Redis] SET ${key} failed:`, error?.message);
      }
      return false;
    }
  }

  /**
   * Delete a key from Redis
   * @param {string} key - The key to delete
   */
  async del(key) {
    if (!(await this.isAvailable())) {
      return false;
    }

    try {
      await this.client.del(key);
      
      if (isDevelopmentMode()) {
        console.debug(`[Redis] DEL ${key}`);
      }
      
      return true;
    } catch (error) {
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[Redis] DEL ${key} failed:`, error?.message);
      }
      return false;
    }
  }

  /**
   * Delete multiple keys matching a pattern
   * @param {string} pattern - The pattern to match (e.g., "user:*")
   */
  async delPattern(pattern) {
    if (!(await this.isAvailable())) {
      return false;
    }

    try {
      const keys = await this.client.keys(pattern);
      if (keys.length > 0) {
        await this.client.del(...keys);
        
        if (isDevelopmentMode()) {
          console.debug(`[Redis] DEL pattern ${pattern}: ${keys.length} keys deleted`);
        }
      }
      
      return true;
    } catch (error) {
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[Redis] DEL pattern ${pattern} failed:`, error?.message);
      }
      return false;
    }
  }

  /**
   * Increment a counter
   * @param {string} key - The key to increment
   * @returns {Promise<number>} The new value
   */
  async incr(key) {
    if (!(await this.isAvailable())) {
      return null;
    }

    try {
      const value = await this.client.incr(key);
      
      if (isDevelopmentMode()) {
        console.debug(`[Redis] INCR ${key}: ${value}`);
      }
      
      return value;
    } catch (error) {
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[Redis] INCR ${key} failed:`, error?.message);
      }
      return null;
    }
  }

  /**
   * Decrement a counter
   * @param {string} key - The key to decrement
   * @returns {Promise<number>} The new value
   */
  async decr(key) {
    if (!(await this.isAvailable())) {
      return null;
    }

    try {
      const value = await this.client.decr(key);
      
      if (isDevelopmentMode()) {
        console.debug(`[Redis] DECR ${key}: ${value}`);
      }
      
      return value;
    } catch (error) {
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[Redis] DECR ${key} failed:`, error?.message);
      }
      return null;
    }
  }

  /**
   * Get keys matching a pattern
   * @param {string} pattern - The pattern to match (e.g., "user:*")
   * @returns {Promise<string[]>} Array of matching keys
   */
  async keys(pattern) {
    if (!(await this.isAvailable())) {
      return [];
    }

    try {
      const keys = await this.client.keys(pattern);
      
      if (isDevelopmentMode()) {
        console.debug(`[Redis] KEYS ${pattern}: ${keys.length} keys found`);
      }
      
      return keys || [];
    } catch (error) {
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[Redis] KEYS ${pattern} failed:`, error?.message);
      }
      return [];
    }
  }

  /**
   * Set expiration time on a key
   * @param {string} key - The key to expire
   * @param {number} seconds - Seconds until expiration
   */
  async expire(key, seconds) {
    if (!(await this.isAvailable())) {
      return false;
    }

    try {
      await this.client.expire(key, seconds);
      
      if (isDevelopmentMode()) {
        console.debug(`[Redis] EXPIRE ${key}: ${seconds}s`);
      }
      
      return true;
    } catch (error) {
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[Redis] EXPIRE ${key} failed:`, error?.message);
      }
      return false;
    }
  }

  /**
   * Get time to live for a key
   * @param {string} key - The key to check
   * @returns {Promise<number>} Seconds until expiration, -1 if no expiration, -2 if key doesn't exist
   */
  async ttl(key) {
    if (!(await this.isAvailable())) {
      return -2;
    }

    try {
      const ttl = await this.client.ttl(key);
      return ttl;
    } catch (error) {
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[Redis] TTL ${key} failed:`, error?.message);
      }
      return -2;
    }
  }

  /**
   * Check if a key exists
   * @param {string} key - The key to check
   * @returns {Promise<boolean>}
   */
  async exists(key) {
    if (!(await this.isAvailable())) {
      return false;
    }

    try {
      const exists = await this.client.exists(key);
      return exists === 1;
    } catch (error) {
      if (error?.message && !error.message.includes('Failed to fetch')) {
        console.debug(`[Redis] EXISTS ${key} failed:`, error?.message);
      }
      return false;
    }
  }
}

// Export singleton instance
const redisClient = new RedisClient();
export default redisClient;